import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const ACCORDION_DURATION = 400;
const ACCORDION_EASING = 'ease-out';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {Animation | null} */
  #animation = null;

  #isClosing = false;

  #isExpanding = false;

  /** @type {string | null} */
  #savedName = null;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    const content = this.details.querySelector('.details-content');
    return content instanceof HTMLElement ? content : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    this.#cancelAnimation();
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (!this.#shouldAnimate()) return;

    event.preventDefault();
    this.details.style.overflow = 'hidden';

    if (this.#isClosing || !this.details.open) {
      this.#open();
    } else if (this.#isExpanding || this.details.open) {
      this.shrink();
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });

    if (this.#shouldAnimate()) {
      this.details.style.overflow = 'hidden';
      this.shrink();
      return;
    }

    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();

      if (this.#shouldAnimate() && this.details.open) {
        this.details.style.overflow = 'hidden';
        this.shrink();
        return;
      }

      this.details.open = false;
    }
  }

  #shouldAnimate() {
    if (this.dataset.disableAnimationOnDesktop === 'true' && !isMobileBreakpoint()) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return Boolean(this.content);
  }

  /**
   * Smoothly collapses an open accordion. Public so sibling rows in a named
   * group can close without snapping.
   */
  shrink = () => {
    this.#cancelAnimation();
    this.#isClosing = true;
    this.#isExpanding = false;
    this.#detachName();

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    this.details.style.overflow = 'hidden';
    this.#animation = this.details.animate({ height: [startHeight, endHeight] }, {
      duration: ACCORDION_DURATION,
      easing: ACCORDION_EASING,
    });

    this.#animation.onfinish = () => this.#onAnimationFinish(false);
    this.#animation.oncancel = () => {
      this.#isClosing = false;
    };
  };

  #open() {
    this.#closeNamedSiblings();
    this.#detachName();
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.style.overflow = 'hidden';
    this.details.open = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#expand());
    });
  }

  #expand() {
    const content = this.content;
    if (!content) {
      this.#onAnimationFinish(true);
      return;
    }

    this.#cancelAnimation();
    this.#isExpanding = true;
    this.#isClosing = false;

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + content.scrollHeight}px`;

    this.#animation = this.details.animate({ height: [startHeight, endHeight] }, {
      duration: ACCORDION_DURATION,
      easing: ACCORDION_EASING,
    });

    this.#animation.onfinish = () => this.#onAnimationFinish(true);
    this.#animation.oncancel = () => {
      this.#isExpanding = false;
    };
  }

  /**
   * @param {boolean} open
   */
  #onAnimationFinish(open) {
    this.details.open = open;
    this.#animation = null;
    this.#isClosing = false;
    this.#isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
    this.#restoreName();
  }

  #cancelAnimation() {
    if (!this.#animation) return;

    this.#animation.onfinish = null;
    this.#animation.oncancel = null;
    this.#animation.cancel();
    this.#animation = null;
  }

  #detachName() {
    const name = this.details.getAttribute('name');
    if (!name) return;

    this.#savedName = name;
    this.details.removeAttribute('name');
  }

  #restoreName() {
    if (!this.#savedName) return;

    this.details.setAttribute('name', this.#savedName);
    this.#savedName = null;
  }

  /**
   * Closes sibling rows that share a `name`, or that live in a
   * `[data-accordion-exclusive]` group (one panel open at a time).
   */
  #closeNamedSiblings() {
    const name = this.details.getAttribute('name') || this.#savedName;
    if (name) {
      document.querySelectorAll(`details[name="${CSS.escape(name)}"]`).forEach((other) => {
        this.#closeOtherDetails(other);
      });
      return;
    }

    this.#closeExclusiveSiblings();
  }

  #closeExclusiveSiblings() {
    const group = this.closest('[data-accordion-exclusive]');
    if (!group) return;

    group.querySelectorAll('details[open]').forEach((other) => {
      this.#closeOtherDetails(other);
    });
  }

  /**
   * Native <details> toggle fallback (reduced motion / no animation).
   * Skipped while the accordion is disabled (e.g. footer menus on desktop).
   */
  #handleToggle = () => {
    if (!this.details.open) return;
    if (this.#shouldAnimate()) return;

    const isMobile = isMobileBreakpoint();
    if ((isMobile && this.#disableOnMobile) || (!isMobile && this.#disableOnDesktop)) return;

    this.#closeNamedSiblings();
  };

  /**
   * @param {Element} other
   */
  #closeOtherDetails(other) {
    if (other === this.details || !(other instanceof HTMLDetailsElement) || !other.open) return;

    const host = other.closest('accordion-custom');
    if (host instanceof AccordionCustom) {
      host.shrink();
    } else {
      other.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
